import type { Lang } from './categories';

export interface HomeFAQ {
  q: string;
  a: string;
}

export interface HomeContent {
  announcement: string;
  hero: {
    h1: string;
    subtitle: string;
    cta1: string;
    cta2: string;
    trust: string;
    search: string;
  };
  whatIs: { h2: string; copy: string; cta: string };
  popular: { h2: string; sub: string };
  how: { h2: string; steps: string[]; cta: string };
  categoryHub: { h2: string; sub: string };
  comparison: { h2: string; columns: [string, string, string]; rows: [string, string, string][] };
  safety: { h2: string; items: string[]; disclaimer: string };
  latest: { h2: string; sub: string };
  faq: { h2: string; items: HomeFAQ[] };
  final: { h2: string; copy: string; cta: string; secondary: string };
}

export const homeContent: Record<Lang, HomeContent> = {
  en: {
    announcement: 'Independent Resource • Updated Content • Beginner Friendly',
    hero: {
      h1: 'AllChinaBuy Spreadsheet – Find Products, Sellers & Shopping Resources',
      subtitle: 'Discover AllChinaBuy spreadsheets, product resources, seller guides, QC tips, shipping information and practical shopping tutorials — all organized in one easy-to-use resource hub.',
      cta1: 'Explore AllChinaBuy Spreadsheet',
      cta2: 'Browse All Guides',
      trust: 'Independent Resource • Updated Content • Beginner Friendly',
      search: 'Search AllChinaBuy products, sellers, guides or spreadsheets...',
    },
    whatIs: {
      h2: 'What Is the AllChinaBuy Spreadsheet?',
      copy: 'The AllChinaBuy Spreadsheet is a convenient way to organize and discover product, seller and shopping information. Our resource hub helps shoppers find useful product references, understand the buying process, compare options and make more informed purchasing decisions.',
      cta: 'Explore the Spreadsheet',
    },
    popular: {
      h2: 'Popular AllChinaBuy Resources',
      sub: 'Start with these reader-favourite guides covering the essentials of shopping through an agent.',
    },
    how: {
      h2: 'How to Use AllChinaBuy Spreadsheet',
      steps: [
        'Find a product or category',
        'Compare available information',
        'Review seller and QC resources',
        'Check shipping information',
        'Make an informed decision',
      ],
      cta: 'Start Exploring',
    },
    categoryHub: {
      h2: 'Explore AllChinaBuy Spreadsheet Categories',
      sub: 'Jump straight to a product category and browse the items you are looking for.',
    },
    comparison: {
      h2: 'AllChinaBuy Compared With Other Shopping Agents',
      columns: ['Feature', 'AllChinaBuy', 'Other Agents'],
      rows: [
        ['Product discovery', '✓', '✓'],
        ['Spreadsheet-style resources', '✓', 'Varies'],
        ['Warehouse service', '✓', '✓'],
        ['QC photos', '✓', 'Varies'],
        ['International shipping', '✓', '✓'],
        ['Beginner guidance', '✓', 'Varies'],
        ['Coupon & promo offers', 'Varies', 'Varies'],
        ['Customer support', 'Varies', 'Varies'],
      ],
    },
    safety: {
      h2: 'Stay Safe When Shopping Online',
      items: [
        'Verify sellers before ordering',
        'Check QC photos carefully',
        'Use safe payment methods',
        'Avoid suspicious links',
        'Be wary of fake coupons',
        'Protect your account security',
        'Keep personal information private',
        'Understand shipping and customs risks',
      ],
      disclaimer: 'This website is an independent informational resource and is not affiliated with or officially endorsed by AllChinaBuy. Always verify current product, seller, payment and shipping information before making a purchase.',
    },
    latest: {
      h2: 'Latest Guides',
      sub: 'Fresh, original guides to help you shop smarter with AllChinaBuy resources.',
    },
    faq: {
      h2: 'Frequently Asked Questions',
      items: [
        { q: 'What is AllChinaBuy Spreadsheet?', a: 'It is a structured way to organize and discover product, seller and shopping information in one place, helping you compare options and make informed decisions.' },
        { q: 'How does AllChinaBuy work?', a: 'You share a product link, the buying agent purchases and stores the item, then you choose a shipping method to have it delivered internationally.' },
        { q: 'Is AllChinaBuy legit?', a: 'AllChinaBuy is a real shopping agent service. As with any service, review its terms, fees and recent feedback before ordering.' },
        { q: 'Is AllChinaBuy safe?', a: 'Using official payment channels, QC photos and keeping records makes the experience safer. Treat it like any online purchase.' },
        { q: 'How much does AllChinaBuy shipping cost?', a: 'It depends on weight, size, destination and the shipping line you choose. Check the live estimate for your parcel.' },
        { q: 'Where can I find AllChinaBuy coupons?', a: 'Check the platform own announcements and promotional campaigns, as offers change frequently.' },
        { q: 'What is the AllChinaBuy warehouse?', a: 'It is the storage facility where your items are held, weighed and prepared for shipping after the agent purchases them.' },
        { q: 'How do AllChinaBuy QC photos work?', a: 'You can request photos of your item at the warehouse to verify it looks correct before it ships internationally.' },
      ],
    },
    final: {
      h2: 'Ready to Explore AllChinaBuy Resources?',
      copy: 'Browse the latest AllChinaBuy spreadsheet resources, shopping guides, shipping information and product research tools.',
      cta: 'Explore AllChinaBuy Spreadsheet',
      secondary: 'Visit Main Website',
    },
  },
  fr: {
    announcement: 'Ressource indépendante • Contenu à jour • Adapté aux débutants',
    hero: {
      h1: 'AllChinaBuy Spreadsheet – Produits, vendeurs et ressources shopping',
      subtitle: "Découvrez les tableurs AllChinaBuy, les ressources produits, les guides vendeurs, les conseils QC, les infos de livraison et les tutoriels pratiques — le tout organisé dans un hub facile à utiliser.",
      cta1: 'Explorer le Spreadsheet AllChinaBuy',
      cta2: 'Parcourir tous les guides',
      trust: 'Ressource indépendante • Contenu à jour • Adapté aux débutants',
      search: 'Rechercher produits, vendeurs, guides ou tableurs AllChinaBuy...',
    },
    whatIs: {
      h2: "Qu'est-ce que le Spreadsheet AllChinaBuy ?",
      copy: "Le Spreadsheet AllChinaBuy est une façon pratique d'organiser et de découvrir les informations produits, vendeurs et shopping. Notre hub aide les acheteurs à trouver des références utiles, à comprendre le processus et à comparer les options.",
      cta: 'Explorer le Spreadsheet',
    },
    popular: {
      h2: 'Ressources AllChinaBuy populaires',
      sub: "Commencez par ces guides préférés des lecteurs, qui couvrent l'essentiel de l'achat via agent.",
    },
    how: {
      h2: 'Comment utiliser le Spreadsheet AllChinaBuy',
      steps: [
        'Trouvez un produit ou une catégorie',
        'Comparez les informations disponibles',
        'Consultez les ressources vendeurs et QC',
        'Vérifiez les informations de livraison',
        'Prenez une décision éclairée',
      ],
      cta: 'Commencer à explorer',
    },
    categoryHub: {
      h2: 'Explorer les catégories du Spreadsheet AllChinaBuy',
      sub: 'Accédez directement à une catégorie de produits et parcourez les articles recherchés.',
    },
    comparison: {
      h2: 'AllChinaBuy comparé aux autres agents d achat',
      columns: ['Fonctionnalité', 'AllChinaBuy', 'Autres agents'],
      rows: [
        ['Découverte de produits', '✓', '✓'],
        ['Ressources type tableur', '✓', 'Variable'],
        ['Service d entrepôt', '✓', '✓'],
        ['Photos QC', '✓', 'Variable'],
        ['Livraison internationale', '✓', '✓'],
        ['Accompagnement débutant', '✓', 'Variable'],
        ['Offres promo', 'Variable', 'Variable'],
        ['Support client', 'Variable', 'Variable'],
      ],
    },
    safety: {
      h2: 'Restez prudent lors de vos achats en ligne',
      items: [
        'Vérifiez les vendeurs avant de commander',
        'Contrôlez attentivement les photos QC',
        'Utilisez des moyens de paiement sûrs',
        'Évitez les liens suspects',
        'Méfiez-vous des faux coupons',
        'Protégez votre compte',
        'Gardez vos informations privées',
        'Comprenez les risques de livraison et de douane',
      ],
      disclaimer: "Ce site est une ressource informative indépendante, non affiliée ni officiellement approuvée par AllChinaBuy. Vérifiez toujours les informations actuelles avant tout achat.",
    },
    latest: {
      h2: 'Derniers guides',
      sub: 'Des guides originaux et récents pour acheter plus intelligemment avec les ressources AllChinaBuy.',
    },
    faq: {
      h2: 'Questions fréquentes',
      items: [
        { q: "Qu'est-ce que le Spreadsheet AllChinaBuy ?", a: "Une façon structurée d'organiser et de découvrir les informations produits, vendeurs et shopping au même endroit." },
        { q: 'Comment fonctionne AllChinaBuy ?', a: "Vous partagez un lien, l'agent achète et stocke l'article, puis vous choisissez une méthode de livraison internationale." },
        { q: 'AllChinaBuy est-il fiable ?', a: "C'est un vrai service d'agent. Comme pour tout service, vérifiez conditions, frais et retours récents." },
        { q: 'Est-ce sûr d utiliser AllChinaBuy ?', a: 'Paiement officiel, photos QC et justificatifs rendent l expérience plus sûre.' },
        { q: 'Combien coûte la livraison AllChinaBuy ?', a: 'Cela dépend du poids, de la taille, de la destination et de la ligne choisie.' },
        { q: 'Où trouver des coupons AllChinaBuy ?', a: 'Consultez les annonces officielles, car les offres changent fréquemment.' },
        { q: "Qu'est-ce que l'entrepôt AllChinaBuy ?", a: "L'installation où vos articles sont stockés, pesés et préparés pour l'expédition." },
        { q: 'Comment fonctionnent les photos QC ?', a: "Vous demandez des photos de l'article à l'entrepôt pour vérifier sa conformité avant l'envoi." },
      ],
    },
    final: {
      h2: 'Prêt à explorer les ressources AllChinaBuy ?',
      copy: 'Parcourez les derniers tableurs AllChinaBuy, guides shopping, infos de livraison et outils de recherche produit.',
      cta: 'Explorer le Spreadsheet AllChinaBuy',
      secondary: 'Visiter le site principal',
    },
  },
  da: {
    announcement: 'Uafhængig ressource • Opdateret indhold • Begyndervenlig',
    hero: {
      h1: 'AllChinaBuy Spreadsheet – find produkter, sælgere og ressourcer',
      subtitle: 'Opdag AllChinaBuy-regneark, produktressourcer, sælgerguider, QC-tips, fragtinfo og praktiske vejledninger — alt organiseret i ét brugervenligt hub.',
      cta1: 'Udforsk AllChinaBuy Spreadsheet',
      cta2: 'Gennemse alle guider',
      trust: 'Uafhængig ressource • Opdateret indhold • Begyndervenlig',
      search: 'Søg AllChinaBuy-produkter, sælgere, guider eller regneark...',
    },
    whatIs: {
      h2: 'Hvad er AllChinaBuy Spreadsheet?',
      copy: 'AllChinaBuy Spreadsheet er en praktisk måde at organisere og opdage produkt-, sælger- og shoppinginfo på. Vores hub hjælper købere med at finde nyttige referencer og sammenligne muligheder.',
      cta: 'Udforsk regnearket',
    },
    popular: {
      h2: 'Populære AllChinaBuy-ressourcer',
      sub: 'Start med disse læserfavoritter, der dækker det vigtigste ved køb via agent.',
    },
    how: {
      h2: 'Sådan bruger du AllChinaBuy Spreadsheet',
      steps: [
        'Find et produkt eller en kategori',
        'Sammenlign tilgængelig information',
        'Gennemgå sælger- og QC-ressourcer',
        'Tjek fragtoplysninger',
        'Træf en informeret beslutning',
      ],
      cta: 'Begynd at udforske',
    },
    categoryHub: {
      h2: 'Udforsk AllChinaBuy Spreadsheet-kategorier',
      sub: 'Gå direkte til en produktkategori, og gennemse de varer, du leder efter.',
    },
    comparison: {
      h2: 'AllChinaBuy sammenlignet med andre indkøbsagenter',
      columns: ['Funktion', 'AllChinaBuy', 'Andre agenter'],
      rows: [
        ['Produktopdagelse', '✓', '✓'],
        ['Regnearksressourcer', '✓', 'Varierer'],
        ['Lagerservice', '✓', '✓'],
        ['QC-billeder', '✓', 'Varierer'],
        ['International fragt', '✓', '✓'],
        ['Begynderhjælp', '✓', 'Varierer'],
        ['Kupon- og rabattilbud', 'Varierer', 'Varierer'],
        ['Kundesupport', 'Varierer', 'Varierer'],
      ],
    },
    safety: {
      h2: 'Vær sikker, når du handler online',
      items: [
        'Verificér sælgere inden bestilling',
        'Tjek QC-billeder omhyggeligt',
        'Brug sikre betalingsmetoder',
        'Undgå mistænkelige links',
        'Vær på vagt over for falske kuponer',
        'Beskyt din konto',
        'Hold personlige oplysninger private',
        'Forstå fragt- og toldrisici',
      ],
      disclaimer: 'Dette websted er en uafhængig informationsressource og er ikke tilknyttet eller godkendt af AllChinaBuy. Verificér altid aktuelle oplysninger inden køb.',
    },
    latest: {
      h2: 'Seneste guider',
      sub: 'Friske, originale guider til at handle smartere med AllChinaBuy-ressourcer.',
    },
    faq: {
      h2: 'Ofte stillede spørgsmål',
      items: [
        { q: 'Hvad er AllChinaBuy Spreadsheet?', a: 'En struktureret måde at organisere og opdage produkt-, sælger- og shoppinginfo ét sted.' },
        { q: 'Hvordan virker AllChinaBuy?', a: 'Du deler et link, agenten køber og opbevarer varen, og du vælger en international forsendelsesmetode.' },
        { q: 'Er AllChinaBuy legitimt?', a: 'Det er en rigtig indkøbsagent-service. Gennemgå vilkår, gebyrer og nyere feedback.' },
        { q: 'Er AllChinaBuy sikkert?', a: 'Officiel betaling, QC-billeder og kvitteringer gør oplevelsen sikrere.' },
        { q: 'Hvad koster AllChinaBuy fragt?', a: 'Det afhænger af vægt, størrelse, destination og valgt linje.' },
        { q: 'Hvor finder jeg AllChinaBuy-kuponer?', a: 'Tjek platformens egne meddelelser, da tilbud ændrer sig ofte.' },
        { q: 'Hvad er AllChinaBuy-lageret?', a: 'Faciliteten hvor dine varer opbevares, vejes og klargøres til forsendelse.' },
        { q: 'Hvordan virker QC-billeder?', a: 'Du beder om billeder af varen på lageret for at bekræfte den inden forsendelse.' },
      ],
    },
    final: {
      h2: 'Klar til at udforske AllChinaBuy-ressourcer?',
      copy: 'Gennemse de nyeste AllChinaBuy-regneark, guider, fragtinfo og produktresearchværktøjer.',
      cta: 'Udforsk AllChinaBuy Spreadsheet',
      secondary: 'Besøg hovedwebstedet',
    },
  },
  de: {
    announcement: 'Unabhängige Ressource • Aktueller Inhalt • Einsteigerfreundlich',
    hero: {
      h1: 'AllChinaBuy Spreadsheet – Produkte, Verkäufer & Ressourcen finden',
      subtitle: 'Entdecken Sie AllChinaBuy Spreadsheets, Produktressourcen, Verkäufer-Guides, QC-Tipps, Versandinfos und praktische Tutorials — alles in einem benutzerfreundlichen Hub.',
      cta1: 'AllChinaBuy Spreadsheet entdecken',
      cta2: 'Alle Guides durchstöbern',
      trust: 'Unabhängige Ressource • Aktueller Inhalt • Einsteigerfreundlich',
      search: 'AllChinaBuy-Produkte, Verkäufer, Guides oder Spreadsheets suchen...',
    },
    whatIs: {
      h2: 'Was ist das AllChinaBuy Spreadsheet?',
      copy: 'Das AllChinaBuy Spreadsheet ist eine praktische Möglichkeit, Produkt-, Verkäufer- und Einkaufsinformationen zu organisieren und zu entdecken. Unser Hub hilft Käufern, nützliche Referenzen zu finden und Optionen zu vergleichen.',
      cta: 'Das Spreadsheet entdecken',
    },
    popular: {
      h2: 'Beliebte AllChinaBuy-Ressourcen',
      sub: 'Beginnen Sie mit diesen Leser-Favoriten, die das Wichtigste zum Einkauf über einen Agenten abdecken.',
    },
    how: {
      h2: 'So nutzen Sie das AllChinaBuy Spreadsheet',
      steps: [
        'Ein Produkt oder eine Kategorie finden',
        'Verfügbare Informationen vergleichen',
        'Verkäufer- und QC-Ressourcen prüfen',
        'Versandinformationen checken',
        'Eine informierte Entscheidung treffen',
      ],
      cta: 'Jetzt entdecken',
    },
    categoryHub: {
      h2: 'AllChinaBuy Spreadsheet-Kategorien entdecken',
      sub: 'Springen Sie direkt zu einer Produktkategorie und stöbern Sie in den gesuchten Artikeln.',
    },
    comparison: {
      h2: 'AllChinaBuy im Vergleich zu anderen Einkaufsagenten',
      columns: ['Funktion', 'AllChinaBuy', 'Andere Agenten'],
      rows: [
        ['Produktfindung', '✓', '✓'],
        ['Spreadsheet-Ressourcen', '✓', 'Variiert'],
        ['Lagerservice', '✓', '✓'],
        ['QC-Fotos', '✓', 'Variiert'],
        ['Internationaler Versand', '✓', '✓'],
        ['Einsteigerhilfe', '✓', 'Variiert'],
        ['Coupon- & Rabattangebote', 'Variiert', 'Variiert'],
        ['Kundensupport', 'Variiert', 'Variiert'],
      ],
    },
    safety: {
      h2: 'Sicher online einkaufen',
      items: [
        'Verkäufer vor der Bestellung prüfen',
        'QC-Fotos sorgfältig kontrollieren',
        'Sichere Zahlungsmethoden nutzen',
        'Verdächtige Links vermeiden',
        'Vor gefälschten Coupons hüten',
        'Ihre Kontosicherheit schützen',
        'Persönliche Daten privat halten',
        'Versand- und Zollrisiken verstehen',
      ],
      disclaimer: 'Diese Website ist eine unabhängige Informationsressource und ist weder mit AllChinaBuy verbunden noch offiziell von diesem unterstützt. Prüfen Sie vor dem Kauf stets die aktuellen Informationen.',
    },
    latest: {
      h2: 'Neueste Guides',
      sub: 'Frische, originale Guides für intelligenteres Einkaufen mit AllChinaBuy-Ressourcen.',
    },
    faq: {
      h2: 'Häufig gestellte Fragen',
      items: [
        { q: 'Was ist das AllChinaBuy Spreadsheet?', a: 'Eine strukturierte Möglichkeit, Produkt-, Verkäufer- und Einkaufsinformationen an einem Ort zu organisieren.' },
        { q: 'Wie funktioniert AllChinaBuy?', a: 'Sie teilen einen Link, der Agent kauft und lagert den Artikel, dann wählen Sie eine internationale Versandmethode.' },
        { q: 'Ist AllChinaBuy seriös?', a: 'Es ist ein echter Einkaufsagent-Service. Prüfen Sie Bedingungen, Gebühren und neue Rückmeldungen.' },
        { q: 'Ist AllChinaBuy sicher?', a: 'Offizielle Zahlung, QC-Fotos und Belege machen die Erfahrung sicherer.' },
        { q: 'Wie viel kostet der AllChinaBuy Versand?', a: 'Das hängt von Gewicht, Größe, Ziel und gewählter Linie ab.' },
        { q: 'Wo finde ich AllChinaBuy Coupons?', a: 'Prüfen Sie die eigenen Ankündigungen der Plattform, da sich Angebote häufig ändern.' },
        { q: 'Was ist das AllChinaBuy Lager?', a: 'Die Einrichtung, in der Ihre Artikel gelagert, gewogen und für den Versand vorbereitet werden.' },
        { q: 'Wie funktionieren QC-Fotos?', a: 'Sie fordern Fotos des Artikels im Lager an, um ihn vor dem Versand zu prüfen.' },
      ],
    },
    final: {
      h2: 'Bereit, AllChinaBuy-Ressourcen zu entdecken?',
      copy: 'Stöbern Sie in den neuesten AllChinaBuy Spreadsheets, Guides, Versandinfos und Produktrecherche-Tools.',
      cta: 'AllChinaBuy Spreadsheet entdecken',
      secondary: 'Hauptwebseite besuchen',
    },
  },
};